import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, message, Card, Typography, Pagination, Radio, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { FormConfig } from '../../components/DynamicForm';
import FormConfigEditor from '../../components/FormConfigEditor';
import { formConfigManager } from '../../utils/formConfigManager';
import { getFormConfigs, saveFormConfig, deleteFormConfig, getProtocols, ProtocolItem } from '../../services/api';
import { v4 as uuidv4 } from 'uuid';

const { Title } = Typography;
const { confirm } = Modal;

const FormConfigManagement: React.FC = () => {
  const [configs, setConfigs] = useState<FormConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentConfig, setCurrentConfig] = useState<FormConfig | null>(null);
  const [formType, setFormType] = useState<number>(0); // 0: 项目表单，1: 设备表单
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  
  // 协议列表状态（用于项目表单）
  const [protocols, setProtocols] = useState<ProtocolItem[]>([]);
  const [selectedProtocolId, setSelectedProtocolId] = useState<number | undefined>(undefined);

  // 加载协议列表
  const loadProtocols = async () => {
    try {
      const response = await getProtocols(undefined, 100);
      
      if (response.data.code === 0 && response.data.data) {
        setProtocols(response.data.data);
      }
    } catch (error) {
      console.error('Error loading protocols:', error);
    }
  };

  // 初始加载
  useEffect(() => {
    loadProtocols();
  }, []);

  // 加载表单配置列表
  const loadConfigs = async (type = formType, page = pagination.current, limit = pagination.pageSize) => {
    setLoading(true);
    try {
      const response = await getFormConfigs(type, page, limit);
      if (response.data.code === 0) {
        setConfigs(response.data.data?.items || []);
        setPagination({
          ...pagination,
          current: page,
          total: response.data.data?.total || 0
        });
      } else {
        message.error(response.data.msg || '加载表单配置失败');
      }
    } catch (error) {
      message.error('加载表单配置失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadConfigs();
  }, []);

  // 当表单类型变化时重新加载数据
  useEffect(() => {
    loadConfigs(formType, 1);
  }, [formType]);

  // 处理分页变化
  const handlePageChange = (page: number, pageSize?: number) => {
    loadConfigs(formType, page, pageSize);
  };

  // 处理表单类型变化
  const handleFormTypeChange = (type: number) => {
    setFormType(type);
  };

  // 处理添加表单配置
  const handleAdd = () => {
    setCurrentConfig({
      id: `form_${Date.now()}`,
      name: '新表单',
      description: '表单描述',
      fields: [],
      type: formType,
      protocolId: selectedProtocolId // 传递选中的协议 ID
    });
    setModalVisible(true);
  };

  // 处理编辑表单配置
  const handleEdit = (record: FormConfig) => {
    // 确保编辑时保留原始表单类型，如果原始类型为-1或无效，则设置为0
    const configToEdit = { ...record };
    if (configToEdit.type === -1 || (configToEdit.type !== 0 && configToEdit.type !== 1)) {
      configToEdit.type = 0;
    }
    setCurrentConfig(configToEdit);
    setModalVisible(true);
  };

  // 处理复制表单配置
  const handleDuplicate = async (record: FormConfig) => {
    const newConfig: FormConfig = {
      ...record,
      id: `form_${uuidv4()}`,
      name: `${record.name} (复制)`,
      type: record.type === 0 || record.type === 1 ? record.type : formType
    };

    try {
      const response = await saveFormConfig(newConfig);
      if (response.data.code === 0) {
        message.success('复制成功');
        loadConfigs(formType);
      } else {
        message.error(response.data.msg || '复制失败');
      }
    } catch (error) {
      message.error('复制失败');
      console.error(error);
    }
  };

  // 处理删除表单配置
  const handleDelete = (id: string) => {
    confirm({
      title: '确认删除',
      content: '确定要删除这个表单配置吗？此操作不可恢复。',
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deleteFormConfig(id);
          if (response.data.code === 0) {
            message.success('删除成功');
            loadConfigs(formType);
          } else {
            message.error(response.data.msg || '删除失败');
          }
        } catch (error) {
          message.error('删除失败');
          console.error(error);
        }
      }
    });
  };

  // 处理保存表单配置
  const handleSave = async (config: FormConfig) => {
    try {
      // 确保保存的表单类型有效（0或1）
      const configToSave = { ...config };
      if (configToSave.type !== 0 && configToSave.type !== 1) {
        configToSave.type = 0; // 默认设置为项目表单
      }
      
      const response = await saveFormConfig(configToSave);
      if (response.data.code === 0) {
        message.success('保存成功');
        setModalVisible(false);
        loadConfigs(formType);
      } else {
        message.error(response.data.msg || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
      console.error(error);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '表单名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '字段数量',
      key: 'fieldCount',
      render: (_: any, record: FormConfig) => record.fields.length
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (_: any, record: FormConfig) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={() => handleDuplicate(record)}
          >
            复制
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={2} style={{ margin: 0 }}>表单配置管理</Title>
        <Link to="/dynamic-form-nav">
          <Button icon={<ArrowLeftOutlined />}>
            返回导航页面
          </Button>
        </Link>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <Radio.Group 
              value={formType} 
              onChange={(e) => handleFormTypeChange(e.target.value)}
              buttonStyle="solid"
              style={{ marginBottom: 16 }}
            >
              <Radio.Button value={0}>项目表单</Radio.Button>
              <Radio.Button value={1}>设备表单</Radio.Button>
            </Radio.Group>

            {/* 协议选择器 - 仅项目表单显示 */}
            {formType === 0 && (
              <Select
                placeholder="选择关联的协议（可选）"
                value={selectedProtocolId}
                onChange={setSelectedProtocolId}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = typeof option?.label === 'string' ? option.label : '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {protocols.map(protocol => (
                  <Select.Option key={protocol.id} value={protocol.id} label={`协议 #${protocol.id}`}>
                    <span>{protocol.title || `协议 #${protocol.id}`}</span>
                    {protocol.projectCount !== undefined && (
                      <Tag style={{ marginLeft: 8 }}>项目：{protocol.projectCount}</Tag>
                    )}
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加表单配置
          </Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={configs}
          loading={loading}
          size="small"
          pagination={false}
        />
        
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger
            showTotal={(total) => `共 ${total} 条记录`}
          />
        </div>
      </Card>

      {/* 编辑模态框 */}
      <Modal
        title={currentConfig?.id.includes('form_') && !currentConfig.id.includes('_') ? "添加表单配置" : "编辑表单配置"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1000}
      >
        <FormConfigEditor
          initialConfig={currentConfig || undefined}
          onSave={handleSave}
          formType={formType}
        />
      </Modal>
    </div>
  );
};

export default FormConfigManagement; 